import { ReactNode } from 'react';

type UploadContactListFormFields = 'file' | 'account' | 'identity_id';

export interface OptionalFeatureSchema {
  label: string;
  key: string;
}

export type ContactListType = 'p2p-sms' | 'test' | 'suppression';

export const CONTACT_LIST_TYPE_MAP: Record<number, ContactListType> = {
  4: 'p2p-sms',
  5: 'suppression',
  6: 'test',
} as const;

export const CONTACT_LIST_TYPE_DISPLAY: Record<ContactListType, string> = {
  'p2p-sms': 'P2P SMS',
  suppression: 'Suppression',
  test: 'Test',
} as const;

export interface UploadContactListFormData extends Omit<FormData, 'append'> {
  append(name: UploadContactListFormFields, value: string | Blob): void;
}

type MapContactListFormFields =
  | 'count'
  | 'filename'
  | 'identity_id'
  | 'list_map'
  | 'list_name'
  | 'split_states'
  | 'suppress_cell_phones'
  | 'use_nat_dnc'
  | 'use_state_dnc'
  | 'account'
  | 'ver'
  | 'opt_in'
  | 'uses_rateplan';

export interface MapContactListFormData extends Omit<FormData, 'append'> {
  append(
    name: MapContactListFormFields,
    value: number | string | Blob | boolean | object
  ): void;
}

export type MappingData = Partial<Record<FieldNameOptions, number | string | undefined>>;

export interface ColumnData {
  columnNumber: number;
  data: string[];
}

export type FieldNameOptions =
  | 'title'
  | 'lead_phone'
  | 'extern_id'
  | 'first_name'
  | 'email'
  | 'mid_name'
  | 'last_name'
  | 'suffix'
  | 'address1'
  | 'address2'
  | 'city'
  | 'state'
  | 'zip'
  | 'aux_data1'
  | 'aux_data2'
  | 'aux_data3'
  | 'aux_data4'
  | 'aux_data5';
export interface FieldData {
  title: string;
  name: FieldNameOptions;
  icon: ReactNode;
  isDone?: boolean;
}

export interface ColumnFieldMappingdData extends ColumnData, FieldData {}

export interface UploadContactListResponse {
  filename: string;
  sample: string[][];
}

export interface RateplanBreakdownItem {
  label: string;
  count: number;
  price?: number;
}

export interface ContactList {
  use_state_dnc: 0 | 1;
  leads_cell_suppressed: 0 | 1;
  leads_state_dnc: 0 | 1;
  leads_remaining: number;
  leads_malformed: 0 | 1;
  leads_supplied: 0 | 1;
  leads_cell_dnc: 0 | 1;
  suppress_cell_phones: 1 | 2 | 4 | 5 | 6; // unknown
  leads_invalid: 0 | 1;
  list_name: string;
  integration_id: null;
  upload_by: 'System'; // unknown
  list_state:
    | 'ACTIVE'
    | 'PAUSED'
    | 'PENDING'
    | 'DEFERRED'
    | 'REJECTED'
    | 'AWAITING-CHECK'; // unkown
  Sublists: []; // unknown
  use_nat_dnc: 0 | 1;
  list_id: number;
  account_id: string;
  leads_duplicate: 0 | 1;
  leads_acct_dnc: 0 | 1;
  leads_master_dnc: 0 | 1;
  identity_id: string;
  leads_nat_dnc: 0 | 1;
  leads_loaded: number;
  shared: 0 | 1;
  upload_date?: string; // '2024-08-15 11:09:51'
  uploaded_date?: string; // '2024-08-15 11:09:51'
  isPending?: boolean; // Flag to identify if the list is from pending API
  uses_rateplan?: 0 | 1;
  rateplan_adjusted?: number;
  rateplan_breakdown?: RateplanBreakdownItem[];
  rateplan_savings_perc?: number;
  rateplan_savings_total_count?: number;
  rateplan_savings_total_count_perc?: number;
}

export interface FetchContactListsParams {
  accountNumber: string;
  usesRateplan?: boolean;
}

export type FetchContactListsResponse = ContactList[];

export interface MapContactListResponseData {
  uploaded_by: string;
  list_name: string;
  account_id: string;
  split_states: number;
  pending_list_id: number;
  uploaded_date: string;
  split_timezones: number;
  integration_id: null;
  token: string;
  use_state_dnc: number;
  use_nat_dnc: number;
  list_state: 'ACTIVE' | 'PENDING'; // unkown
  identity_id: string;
  is_mapped_list: number;
  suppress_cell_phones: number; // unknown - response has value of 4
}

export interface MapContactListResponse {
  Data: MapContactListResponseData;
}

export interface TestContactPhone {
  first_name: string | null;
  last_name: string | null;
  mid_name: string | null;
  lead_phone: string;
  title: string | null;
  suffix: string | null;
  extern_id: string | null;
  email: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  timezone: string;
  gate_keeper: string;
  aux_data1: string | null;
  aux_data2: string | null;
  aux_data3: string | null;
  aux_data4: string | null;
  aux_data5: string | null;
  sublist_id: number;
}

export interface TestContactList {
  identity_id: string;
  list_name: string;
  account_id: string;
  upload_date: string;
  list_state: string;
  upload_by: string;
  list_id: number;
  test_contact_phones: TestContactPhone[];
}

export type TestContactListsResponse = TestContactList[];

// Delete API types
export interface BulkDeleteContactListsParams {
  list_ids: string[];
  force: boolean;
}

export interface BulkDeleteContactListsResponse {
  unlinked: string;
}

export interface SingleDeleteContactListResponse {
  unlinked: string;
}

// Contact List Details types
export interface ContactListSublist {
  leads_remaining: number;
  leads_job_suppressed: number;
  sublist_name: string;
  timezone: string;
  leads_job_duplicate: number;
  leads_donotcall: number;
  sublist_id: number;
  leads_complete: number;
  sublist_state: 'ACTIVE' | 'PAUSED' | 'PENDING' | 'DEFERRED' | 'AWAITING-CHECK';
  leads_duplicate: number;
  leads_loaded: number;
  list_id: number;
}

export interface ContactListDetails {
  leads_supplied: number;
  list_name: string;
  suppress_cell_phones: number;
  use_nat_dnc: 0 | 1;
  Sublists: ContactListSublist[];
  leads_nat_dnc: number;
  upload_date: string;
  integration_id: string | null;
  leads_loaded: number;
  account_id: string;
  leads_duplicate: number;
  shared: 0 | 1;
  leads_master_dnc: number;
  leads_invalid: number;
  leads_cell_suppressed: number;
  identity_id: string;
  leads_state_dnc: number;
  leads_acct_dnc: number;
  upload_by: string;
  leads_remaining: number;
  list_state: 'ACTIVE' | 'PAUSED' | 'PENDING' | 'DEFERRED' | 'AWAITING-CHECK';
  leads_malformed: number;
  leads_cell_dnc: number;
  is_reusable: 0 | 1;
  use_state_dnc: 0 | 1;
  list_id: number;
  uses_rateplan?: 0 | 1;
  rateplan_adjusted?: number;
  rateplan_breakdown?: RateplanBreakdownItem[];
  rateplan_savings_perc?: number;
  rateplan_savings_total_count?: number;
  rateplan_savings_total_count_perc?: number;
}

export interface FetchContactListDetailsParams {
  listId: string;
}

export type FetchContactListDetailsResponse = ContactListDetails;
